import CancelIcon from '@mui/icons-material/Cancel';
import { Box, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { bloxValues } from 'themes/constants';

/**
 * Custom Chip
 *
 * Mock chip: real mui chip calls stopPropagation internally onDelete
 * which prevents ClickAwayListeners from capturing the event to close
 * the popper, this mock chip restores natural event flow.
 *
 */
type DynamicFieldChipProps = {
  idTag: string;
  value: string;
  label: string;
  onRemove: (value: string) => void;
  disabled?: boolean;
};

export const DynamicFieldChip = memo(
  ({
    idTag = 'tag',
    value,
    label,
    onRemove,
    disabled = false,
  }: DynamicFieldChipProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const strRemove = t('blox.actions.remove');
    const removeTxt = `${strRemove}: ${label}`;
    const boxId = `chip-${idTag}-${label}-${value}`;
    const rmvId = `remove-${boxId}`;

    const handleOnRemove = useCallback(() => {
      onRemove(value);
    }, [onRemove, value]);

    return (
      <Box
        data-testid={boxId}
        title={label}
        tabIndex={-1}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.25,
          borderRadius: 3,
          backgroundColor: theme.palette.action.selected,
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <Typography
          sx={{
            fontSize: bloxValues.smallFontSizeRem,
            color: theme.palette.text.primary,
          }}
        >
          {label}
        </Typography>
        <IconButton
          data-testid={rmvId}
          aria-label={removeTxt}
          disabled={disabled}
          onClick={handleOnRemove}
          size="small"
          sx={{
            p: 0,
            color: theme.palette.info.light,
            '&:hover, &:focus-within': {
              color: theme.palette.error.main,
            },
          }}
        >
          <CancelIcon
            titleAccess={removeTxt}
            sx={{
              color: 'inherit',
              fontSize: bloxValues.iconSizeSmall,
            }}
          />
        </IconButton>
      </Box>
    );
  },
);
DynamicFieldChip.displayName = 'DynamicFieldChip';
